import { useMemo, useState } from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, getCitationFilePath, SpeechConfig, SupplementalGraphEvidenceItem } from "../../api";
import { getEvidenceGraphAssessmentIds } from "../EvidenceGraph";
import { extractFollowupQuestionsFromAnswerText, parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onEvidenceGraphClicked?: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    compact?: boolean;
}

const supplementalDateLabel = (value?: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }
    return value.length >= 10 ? value.slice(0, 10) : value;
};

const SupplementalGraphEvidenceItemRow = ({
    item,
    onCitationClicked
}: {
    item: SupplementalGraphEvidenceItem;
    onCitationClicked: (filePath: string) => void;
}) => {
    const dateLabel = supplementalDateLabel(item.assessment_date);
    const spanTexts = (item.evidence_spans ?? []).map(span => span.text).filter((text): text is string => Boolean(text?.trim())).slice(0, 2);

    return (
        <li className={styles.supplementalEvidenceItem}>
            <div className={styles.supplementalEvidenceItemHeader}>
                <button type="button" className={styles.supplementalEvidenceNoteButton} onClick={() => onCitationClicked(item.assessment_id)}>
                    {item.citation_label || item.assessment_id}
                </button>
                <span className={styles.supplementalEvidenceMeta}>
                    {[item.note_type, dateLabel].filter(Boolean).join(" · ")}
                </span>
            </div>
            {item.relationship_summary && <div className={styles.supplementalEvidenceSummary}>{item.relationship_summary}</div>}
            {!!spanTexts.length && (
                <ul className={styles.supplementalEvidenceSpans}>
                    {spanTexts.map((text, index) => (
                        <li key={`${item.assessment_id}-span-${index}`}>{text}</li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onEvidenceGraphClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    compact
}: Props) => {
    const followupQuestions = useMemo(() => {
        const questions = new Set<string>();
        (answer.context?.followup_questions ?? []).forEach(question => question && questions.add(question));
        extractFollowupQuestionsFromAnswerText(answer.message?.content).forEach(question => questions.add(question));
        return Array.from(questions);
    }, [answer]);
    const costSummary = answer.context?.cost_summary;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer, isStreaming, onCitationClicked]);
    const { t } = useTranslation();
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const costSummaryModelLabel = costSummary?.provider_name ? `${costSummary.model} via ${costSummary.provider_name}` : costSummary?.model;
    const [copied, setCopied] = useState(false);
    const [citationsOpen, setCitationsOpen] = useState(true);
    const [followupsOpen, setFollowupsOpen] = useState(!compact);
    const [supplementalEvidenceOpen, setSupplementalEvidenceOpen] = useState(false);
    const hasEvidenceGraph = getEvidenceGraphAssessmentIds(answer).length > 0;
    const supplementalGraphEvidence = answer.context?.data_points?.supplemental_graph_evidence;
    const supplementalGraphEvidenceItems =
        supplementalGraphEvidence?.status === "available" ? (supplementalGraphEvidence.items ?? []).filter(item => item.assessment_id) : [];
    const shouldShowFollowupQuestions = !isStreaming && followupQuestions.length > 0 && Boolean(showFollowupQuestions && onFollowupQuestionClicked);

    const handleCopy = () => {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = sanitizedAnswerHtml;
        tempElement.querySelectorAll("sup").forEach(node => node.remove());
        tempElement.querySelectorAll(".citationStepBadge").forEach(node => node.remove());
        const textToCopy = tempElement.textContent ?? "";

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title={t("tooltips.showThoughtProcess")}
                            ariaLabel={t("tooltips.showThoughtProcess")}
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.context.thoughts?.length || isStreaming}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title={t("tooltips.showSupportingContent")}
                            ariaLabel={t("tooltips.showSupportingContent")}
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.context.data_points || isStreaming}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "BranchMerge" }}
                            title={t("tooltips.showEvidenceGraph")}
                            ariaLabel={t("tooltips.showEvidenceGraph")}
                            onClick={() => onEvidenceGraphClicked?.()}
                            disabled={!hasEvidenceGraph || !onEvidenceGraphClicked || isStreaming}
                        />
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown children={sanitizedAnswerHtml} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} />
                </div>
            </Stack.Item>

            {!isStreaming && !!parsedAnswer.citations.length && (
                <Stack.Item>
                    <button
                        type="button"
                        className={styles.collapsibleToggle}
                        onClick={() => setCitationsOpen(!citationsOpen)}
                        aria-expanded={citationsOpen}
                    >
                        <span className={`${styles.collapsibleChevron} ${citationsOpen ? styles.collapsibleChevronOpen : ""}`} aria-hidden="true" />
                        <span>
                            {t("citationWithColon")} ({parsedAnswer.citations.length})
                        </span>
                    </button>
                    {citationsOpen && (
                        <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                            {parsedAnswer.citations.map(citation => {
                                const isWeb = citation.isWeb;
                                const displayIndex = citation.index;
                                const reference = citation.reference;
                                if (isWeb) {
                                    // Attempt to find the matching web data point to retrieve its title
                                    const webEntry = answer.context.data_points.external_results_metadata?.find(w => w.url === reference);
                                    const titleOrUrl = webEntry?.title?.trim() ? webEntry.title : reference;
                                    return (
                                        <span key={`${reference}-${displayIndex}`} className={styles.citationEntry}>
                                            <a className={styles.citation} title={reference} href={reference} target="_blank" rel="noopener noreferrer">
                                                {`${displayIndex}. ${titleOrUrl}`}
                                            </a>
                                        </span>
                                    );
                                } else {
                                    const citationIds = answer.context.data_points.citation_ids ?? {};
                                    const assessmentId = citationIds[reference];
                                    const path = assessmentId || getCitationFilePath(reference);

                                    return (
                                        <span key={`${reference}-${displayIndex}`} className={styles.citationEntry}>
                                            <a
                                                className={styles.citation}
                                                title={reference}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    onCitationClicked(path);
                                                }}
                                            >
                                                {`${displayIndex}. ${reference}`}
                                            </a>
                                        </span>
                                    );
                                }
                            })}
                        </Stack>
                    )}
                </Stack.Item>
            )}

            {shouldShowFollowupQuestions && (
                <Stack.Item>
                    {compact && (
                        <button
                            type="button"
                            className={styles.collapsibleToggle}
                            onClick={() => setFollowupsOpen(!followupsOpen)}
                            aria-expanded={followupsOpen}
                        >
                            <span className={`${styles.collapsibleChevron} ${followupsOpen ? styles.collapsibleChevronOpen : ""}`} aria-hidden="true" />
                            <span>
                                {t("followupQuestions")} ({followupQuestions.length})
                            </span>
                        </button>
                    )}
                    {(!compact || followupsOpen) && (
                        <Stack horizontal wrap className={styles.followupQuestionsList} tokens={{ childrenGap: 6 }}>
                            {!compact && <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>}
                            {followupQuestions.map((x, i) => {
                                return (
                                    <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked?.(x)}>
                                        {`${x}`}
                                    </a>
                                );
                            })}
                        </Stack>
                    )}
                </Stack.Item>
            )}

            {!isStreaming && !!supplementalGraphEvidenceItems.length && (
                <Stack.Item>
                    <div className={styles.supplementalEvidenceSection}>
                        <button
                            type="button"
                            className={styles.supplementalEvidenceToggle}
                            onClick={() => setSupplementalEvidenceOpen(!supplementalEvidenceOpen)}
                            aria-expanded={supplementalEvidenceOpen}
                        >
                            <span>{supplementalEvidenceOpen ? "Hide" : "Show"}</span>
                            <span>
                                {supplementalGraphEvidence?.presentation?.label || "Additional graph-supported notes for review"} (
                                {supplementalGraphEvidenceItems.length})
                            </span>
                        </button>
                        {supplementalEvidenceOpen && (
                            <div className={styles.supplementalEvidenceBody}>
                                <p className={styles.supplementalEvidenceHelp}>
                                    {supplementalGraphEvidence?.presentation?.helper_text ||
                                        "These notes were found through patient-bounded graph relationships and may add context. They are separate from the citations used in the answer."}
                                </p>
                                <ul className={styles.supplementalEvidenceList}>
                                    {supplementalGraphEvidenceItems.map(item => (
                                        <SupplementalGraphEvidenceItemRow
                                            key={item.assessment_id}
                                            item={item}
                                            onCitationClicked={onCitationClicked}
                                        />
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </Stack.Item>
            )}

            {costSummary && !isStreaming && (
                <Stack.Item>
                    <div
                        className={styles.costSummaryBar}
                        title={
                            costSummary.timing
                                ? `Retrieval: ${((costSummary.timing.retrieval_total_ms ?? 0) / 1000).toFixed(1)}s` +
                                  ` (rewrite ${((costSummary.timing.query_rewrite_ms ?? 0) / 1000).toFixed(1)}s` +
                                  ` + embed ${((costSummary.timing.embedding_ms ?? 0) / 1000).toFixed(1)}s` +
                                  ` + search ${((costSummary.timing.search_ms ?? costSummary.timing.agentic_retrieval_ms ?? 0) / 1000).toFixed(1)}s)` +
                                  ` | LLM: ${((costSummary.timing.llm_generation_ms ?? 0) / 1000).toFixed(1)}s` +
                                  (costSummary.timing.llm_ttft_ms ? ` (TTFT ${(costSummary.timing.llm_ttft_ms / 1000).toFixed(1)}s)` : "")
                                : costSummary.disclaimer || ""
                        }
                    >
                        <span className={styles.costSummaryText}>
                            {costSummary.timing?.total_backend_ms != null && costSummary.timing.total_backend_ms > 0 ? (
                                <>
                                    ⏱️ {(costSummary.timing.total_backend_ms / 1000).toFixed(1)}s
                                    {costSummary.timing.llm_ttft_ms != null && <> (TTFT {(costSummary.timing.llm_ttft_ms / 1000).toFixed(1)}s)</>}
                                    {" · "}
                                </>
                            ) : costSummary.elapsed_ms != null && costSummary.elapsed_ms > 0 ? (
                                <>⏱️ {(costSummary.elapsed_ms / 1000).toFixed(1)}s · </>
                            ) : null}
                            📝 {costSummary.total_tokens.toLocaleString()} tokens ({costSummary.total_prompt_tokens.toLocaleString()} in /{" "}
                            {costSummary.total_completion_tokens.toLocaleString()} out
                            {costSummary.total_reasoning_tokens > 0 && ` / ${costSummary.total_reasoning_tokens.toLocaleString()} reasoning`})
                            {costSummary.estimated_cost != null && (
                                <> · ~${costSummary.estimated_cost < 0.001 ? costSummary.estimated_cost.toFixed(4) : costSummary.estimated_cost.toFixed(3)}</>
                            )}{" "}
                            · {costSummaryModelLabel}
                        </span>
                    </div>
                </Stack.Item>
            )}
        </Stack>
    );
};
